import { eUSAState } from '../../ui-toolkit/enums/states';
import { eTransactionCategory } from 'ui-toolkit/enums/eTransactionType';
import { IPropertyAddress } from '../../ui-toolkit/orders/interfaces/IPropertyAddress';

interface ILenderPropertyAddress extends Omit<IPropertyAddress, 'state'> {
  state_abbreviation: eUSAState;
}

export interface ILenderInformationResponse {
  lender: ILenderInfoResponse;
  address: ILenderPropertyAddress;
  purchasePageCounts: IPurchasePageCountsResponse;
  refiPageCounts: IRefiPageCountsResponse;
}

export interface ILenderInformationData {
  lender: ILenderInfoData;
  address: IPropertyAddress;
  purchasePageCounts: IPurchasePageCountsData;
  refiPageCounts: IRefiPageCountsData;
}

export const convertFromLenderInformationResponseToLenderInformationData = (
  data: ILenderInformationResponse
): ILenderInformationData => ({
  lender: convertFromLenderInfoResponseToLenderInfoData(data.lender),
  address: { ...data.address, state: data.address.state_abbreviation },
  purchasePageCounts: convertFromPurchasePageCountsResponseToPurchasePageCountsData(data.purchasePageCounts),
  refiPageCounts: convertFromRefiPageCountsResponseToRefiPageCountsData(data.refiPageCounts),
});

export interface ILenderInfoResponse {
  address_id: string;
  bulk_rate_qualified: boolean;
  co_branded: boolean;
  created_at: string;
  default_fee_type: eLenderFeeType;
  id: string;
  logo_url?: string;
  master_email?: string;
  mortgagee_clause?: string;
  name: string;
  refi_discounted_closing_fee: boolean;
  updated_at: string;
  wfg_id?: string;
}

export interface ILenderInfoData {
  addressId: string;
  bulkRateQualified: boolean;
  coBranded: boolean;
  createdAt: string;
  defaultFeeType: eLenderFeeType;
  id: string;
  logoUrl?: string;
  masterEmail?: string;
  mortgageeClause?: string;
  name: string;
  refiDiscountedClosingFee: boolean;
  updatedAt: string;
  wfgId?: string;
}

export const convertFromLenderInfoResponseToLenderInfoData = (data: ILenderInfoResponse): ILenderInfoData => ({
  addressId: data.address_id,
  bulkRateQualified: data.bulk_rate_qualified,
  coBranded: data.co_branded,
  createdAt: data.created_at,
  defaultFeeType: data.default_fee_type,
  id: data.id,
  logoUrl: data.logo_url,
  masterEmail: data.master_email,
  mortgageeClause: data.mortgagee_clause,
  name: data.name,
  refiDiscountedClosingFee: data.refi_discounted_closing_fee,
  updatedAt: data.updated_at,
  wfgId: data.wfg_id,
});

export interface IPurchasePageCountsResponse {
  id: string;
  created: string;
  last_modified: string;
  lender_id: string;
  assignment_pages?: number;
  deed_pages?: number;
  lien_release_pages?: number;
  memorandum_pages?: number;
  mortgage_pages?: number;
  notice_of_settlement_pages?: number;
  transaction_type: eTransactionCategory;
}

export interface IPurchasePageCountsData {
  id: string;
  created: string;
  lastModified: string;
  lenderId: string;
  assignmentPages?: number;
  deedPages?: number;
  lienReleasePages?: number;
  memorandumPages?: number;
  mortgagePages?: number;
  noticeOfSettlementPages?: number;
  transactionType: eTransactionCategory;
}

const convertFromPurchasePageCountsResponseToPurchasePageCountsData = (
  data: IPurchasePageCountsResponse
): IPurchasePageCountsData => ({
  id: data.id,
  lenderId: data.lender_id,
  lastModified: data.last_modified,
  created: data.created,
  lienReleasePages: data.lien_release_pages,
  memorandumPages: data.memorandum_pages,
  assignmentPages: data.assignment_pages,
  deedPages: data.deed_pages,
  mortgagePages: data.mortgage_pages,
  noticeOfSettlementPages: data.notice_of_settlement_pages,
  transactionType: data.transaction_type,
});

export interface IRefiPageCountsResponse {
  created: string;
  id: string;
  last_modified: string;
  lender_id: string;
  lien_release_pages?: number;
  mortgage_pages?: number;
  assignment_pages?: number;
  deed_pages?: number;
  memorandum_pages?: number;
  notice_of_settlement_pages?: number;
  transaction_type: eTransactionCategory;
}

export interface IRefiPageCountsData {
  created: string;
  id: string;
  lastModified: string;
  lenderId: string;
  lienReleasePages?: number;
  mortgagePages?: number;
  assignmentPages?: number;
  deedPages?: number;
  memorandumPages?: number;
  noticeOfSettlementPages?: number;
  transactionType: eTransactionCategory;
}

const convertFromRefiPageCountsResponseToRefiPageCountsData = (data: IRefiPageCountsResponse): IRefiPageCountsData => ({
  id: data.id,
  lenderId: data.lender_id,
  lastModified: data.last_modified,
  created: data.created,
  lienReleasePages: data.lien_release_pages,
  memorandumPages: data.memorandum_pages,
  assignmentPages: data.assignment_pages,
  deedPages: data.deed_pages,
  mortgagePages: data.mortgage_pages,
  noticeOfSettlementPages: data.notice_of_settlement_pages,
  transactionType: data.transaction_type,
});

export enum eLenderFeeType {
  Premium = 'premium',
  ReportFee = 'report_fee',
}

export const prettyLenderFeeType: Record<eLenderFeeType, string> = {
  [eLenderFeeType.Premium]: 'Premium',
  [eLenderFeeType.ReportFee]: 'Search Report',
};
